import Vue from "vue";
import store from "@/store";
import { i18n } from "@/main.js";

class ApiWordAuthService {
  constructor() {
    this.modalInstance = null;
    this.pendingNavigation = null; // 保存待处理的导航信息（仅存to/from/router，不再存next）
  }

  /**
   * 检查是否需要API工作台验证
   * @param {Object} to - 目标路由
   * @returns {Boolean} 是否需要验证
   */
  needsVerification(to) {
    // 检查是否是API工作台路由
    const isApiWordRoute = to.path.startsWith("/apiWord");

    if (isApiWordRoute) {
      // 检查用户是否已登录
      const token = localStorage.getItem("token") || store.state.isLogin;
      if (!token) {
        return false; // 没有登录，不需要验证（会重定向到登录页）
      }

      // 检查二次验证状态
      const isVerified = store.state.isVerification === "1";
      return !isVerified; // 如果未验证，则需要显示验证弹窗
    }

    return false;
  }

  /**
   * 显示API工作台验证弹窗（核心修改：先阻止导航，保持URL不变）
   * @param {Object} to - 目标路由
   * @param {Object} from - 来源路由
   * @param {Function} next - 路由导航函数
   * @param {VueRouter} router - Vue Router 实例
   */
  async showAuthModal(to, from, next, router) {
    // 第一步：阻止原导航，强制URL保持在原地址（关键！）
    next(false);

    // 保存待处理的导航信息（仅存to/from/router，不再保存next）
    this.pendingNavigation = { to, from, router };

    // 如果已有弹窗实例，直接显示
    if (this.modalInstance) {
      this.modalInstance.showModal = true;
      return;
    }

    // 保存 this 引用
    const self = this;

    try {
      // 动态导入验证组件
      const ApiWorkAuthModalModule = await import(
        "@/layout/top/components/ApiWorkAuthModal.vue"
      );
      const ApiWorkAuthModal = ApiWorkAuthModalModule.default;

      // 创建容器元素
      const container = document.createElement("div");
      document.body.appendChild(container);

      // 创建验证组件实例，继承当前 Vue 实例的上下文
      const ModalConstructor = Vue.extend({
        store, // Vuex store
        i18n, // 国际化实例
        router, // Vue Router 实例
        data() {
          return {
            showModal: true,
          };
        },
        render(h) {
          return h("div", [
            h(ApiWorkAuthModal, {
              props: {
                show: this.showModal,
                externalControl: true, // 由父组件控制关闭
              },
              on: {
                "update:show": (val) => {
                  this.showModal = val;
                },
                "verify-success": this.handleVerifySuccess,
                close: this.handleClose,
              },
            }),
          ]);
        },
        methods: {
          handleVerifySuccess() {
            // 隐藏弹窗
            this.showModal = false;

            // 获取待处理的导航信息
            const navigation = self.pendingNavigation;

            setTimeout(() => {
              // 销毁 Vue 实例
              this.$destroy();
              if (container && container.parentNode) {
                container.parentNode.removeChild(container);
              }
              self.modalInstance = null;

              // 验证成功：主动跳转到目标页（此时URL才会变成目标地址）
              if (navigation && navigation.router && navigation.to) {
                navigation.router.push(navigation.to).catch((err) => {
                  // 捕获重复导航的错误（避免控制台警告）
                  if (err.name !== "NavigationDuplicated") {
                    console.error("跳转目标页失败:", err);
                  }
                });
              }
              self.pendingNavigation = null; // 清空待处理导航
            }, 100);
          },
          handleClose() {
            // 隐藏弹窗
            this.showModal = false;

            // 获取待处理的导航信息
            const navigation = self.pendingNavigation;

            setTimeout(() => {
              // 销毁 Vue 实例
              this.$destroy();
              if (container && container.parentNode) {
                container.parentNode.removeChild(container);
              }
              self.modalInstance = null;

              // 关键修复：关闭弹窗时主动跳回原页面
              if (navigation && navigation.router && navigation.from) {
                // 跳回来源页，避免白屏
                navigation.router
                  .push(navigation.from.fullPath)
                  .catch((err) => {
                    if (err.name !== "NavigationDuplicated") {
                      console.error("返回原页面失败:", err);
                    }
                  });
              }
              self.pendingNavigation = null; // 清空待处理导航
            }, 100);
          },
        },
      });

      // 挂载组件到容器
      this.modalInstance = new ModalConstructor().$mount(container);
      console.log("厉害吧我先进来啦！！！");
    } catch (error) {
      console.error("加载验证组件失败:", error);
      this.pendingNavigation = null; // 异常时清空
    }
  }

  /**
   * 隐藏验证弹窗
   */
  hideAuthModal() {
    if (this.modalInstance) {
      this.modalInstance.showModal = false;

      setTimeout(() => {
        if (
          this.modalInstance &&
          this.modalInstance.$el &&
          this.modalInstance.$el.parentNode
        ) {
          this.modalInstance.$destroy();
          this.modalInstance.$el.parentNode.removeChild(this.modalInstance.$el);
        }
        this.modalInstance = null;
        this.pendingNavigation = null;
      }, 100);
    }
  }
}

// 导出单例实例
const authServiceInstance = new ApiWordAuthService();
export default authServiceInstance;
